import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Home,
  Calendar,
  Settings,
  LogOut,
  MessageCircle,
  Users,
} from 'lucide-react';
import { Verified } from '@mui/icons-material';
import { useAuthStore } from '../stores/auth.store';
import { useCalendarUIStore } from '../stores/calendarUI.store';
import StarField from './StarField';
import { CalendarFlowLogoutConfirmation } from './CalendarFlowLogoutConfirmation';

// Hook for determining current breakpoint
function useBreakpoint() {
  const [breakpoint, setBreakpoint] = useState<'desktop' | 'tablet' | 'mobile'>(
    'desktop'
  );

  useEffect(() => {
    const handleResize = () => {
      const width = document.documentElement.clientWidth;

      if (width >= 1200) {
        setBreakpoint('desktop');
      } else if (width >= 768) {
        setBreakpoint('tablet');
      } else {
        setBreakpoint('mobile');
      }
    };

    // Set initial breakpoint
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Clean up
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return breakpoint;
}

// Helper function to get dynamic greeting based on time
function getTimeBasedGreeting(): string {
  const hour = new Date().getHours();

  if (hour >= 5 && hour < 12) {
    return 'Good Morning';
  } else if (hour >= 12 && hour < 17) {
    return 'Good Afternoon';
  } else if (hour >= 17 && hour < 21) {
    return 'Good Evening';
  } else {
    return 'Good Night';
  }
}

// Sidebar Component - Memoized for performance
const Sidebar = React.memo(() => {
  const navigate = useNavigate();
  const location = useLocation();
  const profile = useAuthStore((state) => state.profile);
  const { user } = useAuth0();
  const bp = useBreakpoint();

  const userName = profile
    ? `${profile.firstName} ${profile.lastName}`
    : user?.name || 'User';

  const getUserInitials = () => {
    if (profile?.firstName && profile?.lastName) {
      return `${profile.firstName[0]}${profile.lastName[0]}`;
    }
    if (profile?.firstName) {
      return profile.firstName[0];
    }
    if (user?.name) {
      const nameParts = user.name.split(' ');
      if (nameParts.length >= 2) {
        return `${nameParts[0][0]}${nameParts[1][0]}`;
      }
      return nameParts[0][0];
    }
    return 'U';
  };

  const userProfileImage = profile?.photoUrl || user?.picture;
  const isPhoneVerified =
    profile?.settings?.verifyMobileData?.isMobileVerified ?? false;

  // Navigation items
  const navItems = [
    {
      text: 'Dashboard',
      icon: <Home size={20} />,
      active: location.pathname === '/dashboard',
      onClick: () => navigate('/dashboard'),
    },
    // {
    //   text: 'Tasks',
    //   icon: <ClipboardList size={20} />,
    //   active: location.pathname === '/tasks',
    //   onClick: () => navigate('/tasks'),
    // },
    {
      text: 'Calendar',
      icon: <Calendar size={20} />,
      active: location.pathname === '/calendar-flow',
      onClick: () => navigate('/calendar-flow'),
    },
    // {
    //   text: 'Reminders',
    //   icon: <Bell size={20} />,
    //   active: location.pathname === '/reminders',
    //   onClick: () => navigate('/reminders'),
    // },
    // {
    //   text: 'Backlogs',
    //   icon: <Clock size={20} />,
    //   active: location.pathname === '/backlogs',
    //   onClick: () => navigate('/backlogs'),
    // },
    {
      text: 'Keysha Chat',
      icon: <MessageCircle size={20} />,
      active: location.pathname === '/chat',
      onClick: () => navigate('/chat'),
    },
    {
      text: 'Contacts',
      icon: <Users size={20} />,
      active: false,
      disabled: true,
      comingSoon: true,
      onClick: () => {}, // Disabled
    },
    {
      text: 'Settings',
      icon: <Settings size={20} />,
      active: location.pathname === '/settings',
      onClick: () => navigate('/settings'),
    },
  ];

  const handleSignOut = () => {
    // Close any open task views first
    const { setShowTaskOverlay } = useCalendarUIStore.getState();
    setShowTaskOverlay(false);

    // Show logout confirmation
    const setShowLogoutConfirmation =
      useAuthStore.getState().setShowLogoutConfirmation;
    setShowLogoutConfirmation(true);
  };

  return (
    <div
      className={`${
        bp === 'desktop' ? 'w-[260px]' : 'w-full max-w-[260px]'
      } rounded-[20px] border-2 border-[#292929] p-4 ${
        bp === 'mobile' ? 'h-full' : 'h-[calc(100vh-30px)]'
      }`}
      style={{
        background:
          'linear-gradient(176deg, rgba(18,18,34,1) 39%, rgba(35,43,56,1) 95%)',
        display: 'flex',
        flexDirection: 'column',
        width: bp === 'desktop' ? 260 : '100%',
      }}
    >
      <div className="mb-6 mt-2 flex items-center">
        <div
          className={`flex cursor-pointer items-center justify-center overflow-hidden rounded-full ${
            bp === 'mobile' ? 'h-8 w-8 text-sm' : 'h-11 w-11'
          } ${!userProfileImage ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white' : ''}`}
          onClick={() => navigate('/dashboard')}
        >
          {userProfileImage ? (
            <img
              src={userProfileImage}
              alt="Profile"
              className="h-full w-full object-cover"
            />
          ) : (
            <span className="font-semibold">{getUserInitials()}</span>
          )}
        </div>
      </div>

      <div className="mb-8">
        <div className="mb-1 text-sm text-white">{getTimeBasedGreeting()}</div>
        <div className="flex items-center gap-2">
          <div
            className={`truncate font-semibold text-white ${
              bp === 'mobile' ? 'text-lg' : 'text-2xl'
            }`}
            title={userName}
            style={{ maxWidth: bp === 'mobile' ? '180px' : '200px' }}
          >
            {userName}
          </div>
          {isPhoneVerified && (
            <Verified
              sx={{
                color: '#1DA1F2',
                fontSize: bp === 'mobile' ? '18px' : '24px',
              }}
            />
          )}
        </div>
      </div>

      <div className="w-full">
        {navItems.map((item, index) => (
          <button
            key={index}
            className={`mb-2 flex w-full items-center justify-between rounded-lg p-3 ${
              item.disabled
                ? 'cursor-not-allowed bg-transparent opacity-50'
                : item.active
                  ? 'bg-white/10'
                  : 'bg-transparent hover:bg-white/5'
            }`}
            onClick={item.disabled ? undefined : item.onClick}
            disabled={item.disabled}
          >
            <div className="flex items-center">
              <div className="min-w-[40px] text-white">{item.icon}</div>
              <div className="text-sm font-semibold text-white">
                {item.text}
              </div>
            </div>
            {item.comingSoon && (
              <div className="rounded-full bg-gray-700 px-2 py-1 text-xs text-gray-400">
                Soon
              </div>
            )}
          </button>
        ))}
      </div>

      <div className="mt-auto">
        <button
          className="flex w-full items-center rounded-lg bg-transparent p-3 hover:bg-white/5"
          onClick={handleSignOut}
        >
          <div className="min-w-[40px] text-white">
            <LogOut size={20} />
          </div>
          <div className="text-sm font-semibold text-white">Sign out</div>
        </button>
      </div>
    </div>
  );
});

interface DashboardLayoutProps {
  children: React.ReactNode;
  showChatButton?: boolean;
}

// Main Dashboard Layout Component
export const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  children,
  showChatButton = true,
}) => {
  const breakpoint = useBreakpoint();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const navigate = useNavigate();

  // Log component mount once
  useEffect(() => {
    console.log('[RedirectDebug] 🔄 DashboardLayout component mounted');
  }, []);

  // Toggle sidebar visibility
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Hide sidebar on tablet and mobile by default
  useEffect(() => {
    if (breakpoint === 'tablet' || breakpoint === 'mobile') {
      setSidebarOpen(false);
    } else {
      setSidebarOpen(true);
    }
  }, [breakpoint]);

  return (
    <div
      className={`flex ${breakpoint === 'desktop' ? 'h-screen' : 'min-h-screen'} bg-[#0F0F1A]`}
    >
      {/* StarField background */}
      <StarField
        brightStarCount={35}
        dimStarCount={75}
        tinyStarCount={100}
        fixed={true}
      />

      {/* Sidebar - conditionally show based on sidebarOpen state */}
      <div
        id="sidebar"
        className={`${
          breakpoint === 'tablet' || breakpoint === 'mobile'
            ? 'fixed left-0 top-0 z-30 h-full'
            : 'relative'
        } transition-all duration-300 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{
          padding:
            breakpoint === 'desktop'
              ? '14px'
              : breakpoint === 'tablet'
                ? '12px'
                : '10px',
        }}
      >
        <Sidebar />
      </div>

      {/* Overlay for tablet/mobile when sidebar is open */}
      {(breakpoint === 'tablet' || breakpoint === 'mobile') && sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black bg-opacity-50"
          onClick={toggleSidebar}
        />
      )}

      {/* Main Content */}
      <div
        className={`flex-1 ${
          breakpoint === 'desktop'
            ? 'p-4 md:p-6 lg:p-8'
            : breakpoint === 'tablet'
              ? 'p-4 md:p-6'
              : 'p-4'
        }`}
      >
        {/* Toggle button for sidebar on tablet/mobile */}
        {(breakpoint === 'tablet' || breakpoint === 'mobile') && (
          <button
            className="mb-6 flex items-center rounded-lg bg-gray-800 p-2 text-white"
            onClick={toggleSidebar}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="mr-2"
            >
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
            Menu
          </button>
        )}

        {children}

        {/* Chat button */}
        {showChatButton && (
          <div className="fixed bottom-6 right-6 z-50">
            <button
              className={`flex items-center justify-center rounded-full bg-[#6B46C1] shadow-lg transition-colors hover:bg-[#553C9A] ${
                breakpoint === 'mobile'
                  ? 'h-[60px] w-[60px]'
                  : 'h-[87px] w-[87px]'
              }`}
              onClick={() => navigate('/chat')}
              aria-label="Open Keysha Chat"
            >
              <span
                className={`font-bold text-white ${
                  breakpoint === 'mobile' ? 'text-xl' : 'text-3xl'
                }`}
              >
                K
              </span>
            </button>
          </div>
        )}
      </div>

      {/* Logout Confirmation Modal - renders globally */}
      <CalendarFlowLogoutConfirmation />
    </div>
  );
};

// Add display names for better debugging
Sidebar.displayName = 'Sidebar';

export { useBreakpoint };
export default DashboardLayout;
